import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';
import { navLinks } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection } from '@hooks';
import { Switch, Menu } from '@components';
import { IconLogo } from '@components/icons';
import { useLanguage } from '@components/context/language';
import { getTranslation } from '@translations';

const StyledLanguageToggle = styled.select`
  color: var(--john-text-mild);
  background-color: transparent;
  border: none;
  border-radius: 0;
  padding: 0.3rem 0.5rem;
  font-size: var(--fz-xxs);
  font-family: var(--font-mono);
  line-height: 1;
  margin-left: 10px;
  cursor: pointer;
  transition: var(--transition);
  min-width: 50px;
  height: auto;

  &:hover,
  &:focus {
    color: var(--john-text-pretty);
    outline: none;
  }

  option {
    background-color: var(--john-bg-moderate);
    color: var(--john-text-mildest);
    font-size: var(--fz-xxs);
  }

  @media (max-width: 768px) {
    margin-left: 5px;
    padding: 0.5rem;
    font-size: var(--fz-sm);
    min-width: 60px;
    background-color: var(--john-bg-mildest);
    border-radius: var(--border-radius);
  }
`;

const LanguageToggle = () => {
  const { language, changeLanguage } = useLanguage();

  const handleLanguageChange = e => {
    const newLang = e.target.value;
    changeLanguage(newLang);
  };

  return (
    <StyledLanguageToggle
      value={language}
      onChange={handleLanguageChange}
      className="shimmer-link"
      aria-label={getTranslation(language, 'nav.language')}
    >
      <option value="en">EN</option>
      <option value="ko">KR</option>
    </StyledLanguageToggle>
  );
};

const bounceArrow = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(4px);
  }
`;

const StyledMobileNav = styled.div`
  display: none;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const StyledSwitchForMobile = styled.div`
  display: block;
`;

const StyledHeader = styled.header`
  ${({ theme }) => theme.mixins.flexBetween};
  position: fixed;
  top: 0;
  z-index: 11;
  padding: 0px 50px;
  width: 100%;
  height: var(--nav-height);
  background-color: var(--john-bg-moderate);
  filter: none !important;
  pointer-events: auto !important;
  user-select: auto !important;
  backdrop-filter: blur(10px);
  transition: var(--transition);

  ${props =>
  props.scrollDirection === 'up' &&
    !props.scrolledToTop &&
    css`
      height: var(--nav-scroll-height);
      transform: translateY(0px);
      background-color: var(--john-nav-color);
      box-shadow: 0 10px 30px -10px var(--john-bg-shadow);
    `};

  ${props =>
      props.scrollDirection === 'down' &&
    !props.scrolledToTop &&
    css`
      height: var(--nav-scroll-height);
      transform: translateY(calc(var(--nav-scroll-height) * -1));
      box-shadow: 0 10px 30px -10px var(--john-bg-shadow);
    `};

  @media (max-width: 1080px) {
    padding: 0 40px;
  }
  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.flexBetween};
  position: relative;
  width: 100%;
  color: var(--john-text-mildest);
  font-family: var(--font-mono);
  counter-reset: item 0;
  z-index: 12;

  .logo {
    ${({ theme }) => theme.mixins.flexCenter};

    a {
      color: var(--john-text-pretty);
      width: 42px;
      height: 42px;

      &:hover,
      &:focus {
        svg {
          fill: var(--john-text-pretty-shadow);
        }
      }

      svg {
        fill: none;
        transition: var(--transition);
        user-select: none;
      }
    }
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }

  ol {
    ${({ theme }) => theme.mixins.flexBetween};
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      margin: 0 5px;
      position: relative;
      counter-increment: item 1;
      font-size: var(--fz-xs);

      a {
        padding: 10px;

        &:before {
          content: '0' counter(item) '.';
          margin-right: 5px;
          color: var(--john-text-pretty);
          font-size: var(--fz-xxs);
          text-align: right;
        }
      }
    }
  }

  .resume-button {
    ${({ theme }) => theme.mixins.smallButton};
    margin-left: 15px;
    font-size: var(--fz-xs);
  }

  .blogs-button {
    color: var(--john-text-pretty);
    background-color: transparent;
    border: 1px solid var(--john-text-pretty);
    border-radius: var(--border-radius);
    padding: 0.75rem 1rem;
    font-size: var(--fz-xs);
    font-family: var(--font-mono);
    line-height: 1;
    text-decoration: none;
    cursor: pointer;
    transition: var(--transition);
    margin-left: 10px;

    &:hover,
    &:focus,
    &:active {
      background-color: var(--john-text-pretty-shadow);
      outline: none;
      transform: translateX(2px);
    }

    .arrow {
      display: inline-block;
      animation: ${bounceArrow} 2s infinite;
    }

    @media (max-width: 949px) {
      display: none;
    }
  }

  .language-dropdown {
    color: var(--john-text-mild);
    background-color: transparent;
    border: none;
    border-radius: 0;
    padding: 0.3rem 0.5rem;
    font-size: var(--fz-xxs);
    font-family: var(--font-mono);
    line-height: 1;
    margin-left: 10px;
    cursor: pointer;
    transition: var(--transition);
    min-width: 50px;
    height: auto;

    &:hover,
    &:focus {
      color: var(--john-text-pretty);
      outline: none;
    }

    option {
      background-color: var(--john-bg-moderate);
      color: var(--john-text-mildest);
      font-size: var(--fz-xxs);
    }
  }
`;

const Nav = ({ isHome, isDarkMode, setIsDarkMode }) => {
  const [isMounted, setIsMounted] = useState(false);
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const { language } = useLanguage();

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50);
  };

  // Animate navbar when coming from navigation button clicks
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    // Check if navbar animation was triggered by navigation button
    if (typeof window !== 'undefined') {
      const animateNav = sessionStorage.getItem('animateNavbar');
      if (animateNav) {
        setShouldAnimate(true);
        sessionStorage.removeItem('animateNavbar');
      }
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeout = shouldAnimate ? loaderDelay : 0;
  const fadeClass = shouldAnimate ? 'fade' : '';
  const fadeDownClass = shouldAnimate ? 'fadedown' : '';

  return (
    <StyledHeader scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <StyledNav>
        <TransitionGroup component={null}>
          {isMounted && (
            <CSSTransition classNames={fadeClass} timeout={timeout}>
              <div className="logo" tabIndex="-1">
                {isHome ? (
                  <a href="/" aria-label="home">
                    <IconLogo />
                  </a>
                ) : (
                  <Link to="/" aria-label="home">
                    <IconLogo />
                  </Link>
                )}
              </div>
            </CSSTransition>
          )}
        </TransitionGroup>
        <StyledMobileNav>
          <StyledSwitchForMobile>
            <Switch isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}></Switch>
          </StyledSwitchForMobile>
          <LanguageToggle />
        </StyledMobileNav>
        <StyledLinks>
          <ol>
            <TransitionGroup component={null}>
              {isMounted &&
                navLinks &&
                navLinks.map(({ url, name }, i) => (
                  <CSSTransition key={i} classNames={fadeDownClass} timeout={timeout}>
                    <li key={i} style={{ transitionDelay: `${i * 100}ms` }}>
                      <Link to={url}>{getTranslation(language, `nav.${name.toLowerCase()}`)}</Link>
                    </li>
                  </CSSTransition>
                ))}
            </TransitionGroup>
          </ol>

          <TransitionGroup component={null}>
            {isMounted && (
              <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                <div style={{ transitionDelay: `${navLinks.length * 100}ms` }}>
                  <a href="/cv.pdf" className="resume-button shimmer-link">
                    {getTranslation(language, 'nav.resume')}
                  </a>
                  <Link
                    to={!isHome ? '/' : '/blog'}
                    className="blogs-button shimmer-link"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        if (!isHome) {
                          // Going back to home from blogs - trigger navbar animation
                          sessionStorage.setItem('animateNavbar', 'true');
                          sessionStorage.setItem('skipLoading', 'true');
                        } else {
                          // Going to blogs from home - trigger navbar animation
                          sessionStorage.setItem('animateNavbar', 'true');
                        }
                      }
                    }}
                  >
                    {!isHome ? (
                      <>
                        <span className="arrow">←</span>
                        {` ${getTranslation(language, 'nav.backToCV')}`}
                      </>
                    ) : (
                      <>
                        {`${getTranslation(language, 'nav.goToBlogs')} `}
                        <span className="arrow">→</span>
                      </>
                    )}
                  </Link>
                </div>
              </CSSTransition>
            )}
            <Switch isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}></Switch>
            <LanguageToggle />
          </TransitionGroup>
        </StyledLinks>

        <TransitionGroup component={null}>
          {isMounted && (
            <CSSTransition classNames={fadeClass} timeout={timeout}>
              <Menu />
            </CSSTransition>
          )}
        </TransitionGroup>
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
  isDarkMode: PropTypes.bool.isRequired,
  setIsDarkMode: PropTypes.func.isRequired,
};

export default Nav;
